import { s, type SpacingToken } from '@/theme/tokens';
import { DialogLayout } from '@/ui';
import { Box, Button, type DialogProps, Text } from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives';
import type { KeyboardEvent, ReactNode } from 'react';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  titleSlot?: ReactNode;
  footerStartSlot?: ReactNode;
  titleSx?: SxProps<Theme>;
  description?: ReactNode;
  children?: ReactNode;
  padding?: SpacingToken | null;
  headerGap?: SpacingToken;
  confirmLabel?: string;
  confirmColor?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'neutral'
    | 'green'
    | 'red'
    | 'error'
    | 'orange'
    | 'brown'
    | 'blue'
    | 'purple';
  confirmVariant?: 'contained' | 'outlined' | 'text';
  confirmDisabled?: boolean;
  loading?: boolean;
  hideCancel?: boolean;
  disableClose?: boolean;
  allowTopBarInteraction?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  maxWidth?: DialogProps['maxWidth'];
  gap?: SpacingToken;
  footerGap?: SpacingToken;
};

export const ConfirmDialog = ({
  open,
  title,
  titleSlot,
  footerStartSlot,
  titleSx,
  description,
  children,
  padding,
  headerGap,
  confirmLabel = 'Confirm',
  confirmColor = 'red',
  confirmVariant = 'contained',
  confirmDisabled = false,
  loading = false,
  hideCancel = false,
  disableClose = false,
  allowTopBarInteraction = false,
  onClose,
  onConfirm,
  maxWidth = 'xs',
  gap,
  footerGap = 'sm',
}: ConfirmDialogProps): ReactNode => {
  const resolvedVariant =
    confirmVariant === 'outlined' ? 'secondary' : confirmVariant === 'text' ? 'ghost' : 'primary';
  const confirmIsCritical = confirmColor === 'error' || confirmColor === 'red';
  const confirmButtonVariant =
    confirmIsCritical && resolvedVariant === 'primary' ? 'destructive' : resolvedVariant;
  const confirmButtonColor =
    confirmIsCritical && resolvedVariant === 'primary' ? undefined : confirmColor;
  const resolvedTitleSx: SxProps<Theme> = titleSx ?? {
    fontSize: (theme) => theme.typography.h3.fontSize,
    lineHeight: (theme) => theme.typography.h3.lineHeight,
  };
  const resolvedDescription =
    typeof description === 'string' ? (
      <Text
        role="body"
        color="textSecondary"
        sx={{
          fontSize: (theme) => theme.typography.subtitle1.fontSize,
          lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
        }}
      >
        {description}
      </Text>
    ) : description ? (
      <Box
        sx={{
          '& .MuiTypography-root': {
            fontSize: (theme) => theme.typography.subtitle1.fontSize,
            lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
          },
        }}
      >
        {description}
      </Box>
    ) : undefined;
  const handleContentKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (confirmIsCritical || loading || confirmDisabled) return;
    if (event.defaultPrevented || event.key !== 'Enter') return;
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName;
    if (tagName === 'TEXTAREA' || target?.isContentEditable) return;
    event.preventDefault();
    onConfirm();
  };
  return (
    <DialogLayout
      open={open}
      onClose={onClose}
      disableClose={disableClose}
      allowTopBarInteraction={allowTopBarInteraction}
      maxWidth={maxWidth}
      title={title}
      titleSlot={titleSlot}
      titleSx={resolvedTitleSx}
      description={resolvedDescription}
      padding={padding}
      headerGap={headerGap}
      onContentKeyDownCapture={handleContentKeyDownCapture}
      actions={
        footerStartSlot ? (
          <>
            {footerStartSlot}
            <Box sx={{ display: 'flex', gap: s('lg') }}>
              {hideCancel ? null : (
                <Button
                  variant="secondary"
                  onClick={onClose}
                  disabled={loading}
                  aria-label="Cancel confirmation"
                >
                  Cancel
                </Button>
              )}
              <Button
                variant={confirmButtonVariant}
                color={confirmButtonColor}
                loading={loading}
                onClick={onConfirm}
                disabled={confirmDisabled}
                aria-label="Confirm action"
              >
                {confirmLabel}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {hideCancel ? null : (
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={loading}
                aria-label="Cancel confirmation"
              >
                Cancel
              </Button>
            )}
            <Button
              variant={confirmButtonVariant}
              color={confirmButtonColor}
              loading={loading}
              onClick={onConfirm}
              disabled={confirmDisabled}
              aria-label="Confirm action"
            >
              {confirmLabel}
            </Button>
          </>
        )
      }
      actionsAlign={footerStartSlot ? 'space-between' : 'flex-end'}
      actionsGap="lg"
      footerGap={footerGap}
      gap={gap}
    >
      {children ?? null}
    </DialogLayout>
  );
};
